/**
 * TopCategoriesChart
 *
 * Top 카테고리 순위 막대차트
 * - 기본 TOP 5 표시
 * - 토글 버튼으로 전체 카테고리 보기/접기 가능
 */

import { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import type { CategoryStat } from '../../types/Statistics'

interface TopCategoriesChartProps {
  data: CategoryStat[]
  onCategoryClick: (stat: CategoryStat) => void
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']

export function TopCategoriesChart({ data, onCategoryClick }: TopCategoriesChartProps) {
  // 토글 상태: false = TOP 5만, true = 전체 표시
  const [showAll, setShowAll] = useState(false)

  // 반응형 Y축 너비
  const [yAxisWidth, setYAxisWidth] = useState(100)

  useEffect(() => {
    const updateYAxisWidth = () => {
      if (window.innerWidth < 640) {
        setYAxisWidth(80)      // 모바일
      } else if (window.innerWidth < 1024) {
        setYAxisWidth(100)     // 태블릿
      } else {
        setYAxisWidth(120)     // 데스크톱
      }
    }
    updateYAxisWidth()
    window.addEventListener('resize', updateYAxisWidth)
    return () => window.removeEventListener('resize', updateYAxisWidth)
  }, [])

  // 표시할 데이터 결정
  const displayData = showAll ? data : data.slice(0, 5)

  // 차트 데이터 변환
  const chartData = displayData.map(stat => ({
    name: `${stat.categoryIcon} ${stat.categoryName}`,
    amount: stat.amount,
    stat
  }))

  // 동적 높이 계산 (최소 250px, 카테고리당 50px)
  const chartHeight = Math.max(250, displayData.length * 50)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      {/* 헤더: 제목 + 토글 버튼 */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
          지출 TOP {showAll ? data.length : Math.min(5, data.length)}
        </h3>

        {/* 토글 버튼 (카테고리가 5개 초과일 때만 표시) */}
        {data.length > 5 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
            aria-label={showAll ? '카테고리 목록 접기' : '전체 카테고리 보기'}
            aria-expanded={showAll}
          >
            {showAll ? '접기 ▲' : '전체 보기 ▼'}
          </button>
        )}
      </div>

      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            onClick={(chartData) => chartData?.activePayload?.[0] && onCategoryClick(chartData.activePayload[0].payload.stat)}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={yAxisWidth} />
            <Tooltip
              formatter={(value: number) => `${value.toLocaleString()}원`}
            />
            <Bar dataKey="amount" radius={[0, 8, 8, 0]}>
              {chartData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
          지출 데이터가 없습니다
        </div>
      )}
    </div>
  )
}
